import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addContractorTradeTerms,
  editTradeTerm,
  getContractorServiceTypesTradeTerm,
  getContractorTradeTerms,
  removeTradeTerm,
} from "@/requests/administration/contractors/contractorAdministrationCommissionsAndDiscounts";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import _ from "lodash";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  ContractorServiceTypeTradeTerm,
  ContractorServiceTypeTradeTermResponse,
} from "@/models/api/administration/contractors/ContractorServiceTypeTradeTermResponse";
import { ContractorTradeTerm } from "@/models/api/administration/contractors/ContractorTradeTerm";
import { ContractorTradeTermAddEdit } from "@/models/api/administration/contractors/ContractorTradeTermAddEdit";

export interface AdministrationContractorCommissionsAndDiscountsInterface {
  isAddContractorTradeTermDialog: boolean;
  countItems: number;
  dataTableOptions: DataOptions;
  filter: null | Array<CriterionBase>;
  serviceTypeItems: Array<ContractorServiceTypeTradeTerm>;
  contractorCommissionsAndDiscounts: Array<ContractorTradeTerm>;
  addEditModel: ContractorTradeTermAddEdit;
  tradeTermId: string | undefined;
  selectedServiceTypeId: null | number;
}

export const AdministrationContractorCommissionsAndDiscountsState: AdministrationContractorCommissionsAndDiscountsInterface =
  {
    isAddContractorTradeTermDialog: false,
    countItems: 0,
    dataTableOptions: new DefaultDataOptions(),
    filter: null,
    serviceTypeItems: [],
    contractorCommissionsAndDiscounts: [],
    addEditModel: new ContractorTradeTermAddEdit(),
    tradeTermId: undefined,
    selectedServiceTypeId: null,
  };

const AdministrationContractorCommissionsAndDiscountsModule = defineModule({
  state:
    AdministrationContractorCommissionsAndDiscountsState as AdministrationContractorCommissionsAndDiscountsInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorsCommissionAndDiscountFilters(state, param: Array<CriterionBase>) {
      state.filter = param;
    },
    setContractorCommissionAndDiscounts(state, param: ContractorServiceTypeTradeTermResponse) {
      state.serviceTypeItems = param.items;
      state.countItems = param.totalCount;
      if (param.items.length > 0) state.selectedServiceTypeId = param.items[0].id;
      else state.selectedServiceTypeId = null;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setIsAddContractorTradeTermDialog(state, param: boolean) {
      state.isAddContractorTradeTermDialog = param;
    },
    setContractorCommissionsAndDiscounts(state, param: Array<ContractorTradeTerm>) {
      state.contractorCommissionsAndDiscounts = param;
    },
    setAddEditModelAndTradeTermId(
      state,
      param: {
        model: ContractorTradeTermAddEdit;
        tradeTermId: string | undefined;
      }
    ) {
      state.addEditModel = param.model;
      state.tradeTermId = param.tradeTermId;
    },
    setSelectedServiceTypeId(state, param: null | number) {
      state.selectedServiceTypeId = param;
    },
    setAddEditModel(state, param: ContractorTradeTermAddEdit) {
      state.addEditModel = param;
    },
  },
  actions: {
    async downloadContractorServiceTypes(context) {
      const { commit, rootState, state } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const contractorCommissionsAndDiscounts = await getContractorServiceTypesTradeTerm(
          {
            count: state.dataTableOptions.itemsPerPage,
            page: state.dataTableOptions.page - 1,
            criterions: _.cloneDeep(state.filter),
            filterName: FILTER_NAMES.ServiceTypeSaleListFilter,
          },
          rootState.administration.contractors.list.selectedContractorId as string
        );
        commit.setContractorCommissionAndDiscounts(contractorCommissionsAndDiscounts);
      }
    },
    async downloadContractorCommissionsAndDiscounts(context) {
      const { commit, rootState, state } = _moduleActionContext(context);
      if (
        rootState.administration.contractors.list.selectedContractorId !== null &&
        state.selectedServiceTypeId !== null
      ) {
        const result = await getContractorTradeTerms(
          rootState.administration.contractors.list.selectedContractorId as string,
          state.selectedServiceTypeId as number
        );
        commit.setContractorCommissionsAndDiscounts(result);
      } else commit.setContractorCommissionsAndDiscounts([]);
    },
    async addContractorTradeTerm(context) {
      const { state, rootState, dispatch, commit, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId && state.selectedServiceTypeId) {
        await addContractorTradeTerms(
          rootState.administration.contractors.list.selectedContractorId,
          state.selectedServiceTypeId,
          state.addEditModel
        );
        const temp = state.selectedServiceTypeId as number;
        await dispatch.downloadContractorServiceTypes();
        commit.setSelectedServiceTypeId(temp);
        await rootDispatch.waitForCompleteAllRequests();
        await dispatch.downloadContractorCommissionsAndDiscounts();
      }
    },
    async editContractorTradeTerm(context) {
      const { dispatch, state } = _moduleActionContext(context);
      await editTradeTerm(state.tradeTermId as string, state.addEditModel);
      dispatch.downloadContractorCommissionsAndDiscounts();
    },
    async removeContractorTradeTerm(context, tradeTermId: string) {
      const { dispatch } = _moduleActionContext(context);
      await removeTradeTerm(tradeTermId);
      dispatch.downloadContractorServiceTypes();
      dispatch.downloadContractorCommissionsAndDiscounts();
    },
  },
});

export default AdministrationContractorCommissionsAndDiscountsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(
    args,
    AdministrationContractorCommissionsAndDiscountsModule
  );
const _moduleActionContext = (context: any) =>
  moduleActionContext(
    context,
    AdministrationContractorCommissionsAndDiscountsModule
  );
